// Reusable windowed pagination.
// Shows the first and last page, a small window of pages around the current
// one, and ellipses for the gaps — so a list with hundreds of pages renders
// a handful of buttons instead of overflowing the row.
//
// Props:
//   page        – current page (1-based)
//   totalPages  – total number of pages
//   onChange    – called with the new page number (already clamped 1..total)
//   style       – optional inline style for the container
//   leftSlot    – optional content rendered on the LEFT of the bar (e.g. a
//                 total-count label). When given, the bar uses space-between
//                 so the page controls stay on the right.
export default function Pagination({ page, totalPages, onChange, style, leftSlot }) {
  // Nothing to show: hide entirely unless there's a leftSlot worth keeping.
  if (totalPages <= 1 && !leftSlot) return null;

  const go = (p) => onChange(Math.min(Math.max(p, 1), totalPages));

  // Build [1, …, left..right, …, total] with the current page centred.
  const delta = 1; // pages shown on each side of the current page
  const left = Math.max(2, page - delta);
  const right = Math.min(totalPages - 1, page + delta);

  const pages = [1];
  if (left > 2) pages.push("…");
  for (let i = left; i <= right; i++) pages.push(i);
  if (right < totalPages - 1) pages.push("…");
  pages.push(totalPages);

  const nav = totalPages > 1 && (
    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
      <button
        className="pg-btn"
        onClick={() => go(page - 1)}
        disabled={page === 1}
        aria-label="Previous page"
      >
        <i className="ti ti-chevron-left" style={{ fontSize: "12px" }}></i>
      </button>

      {pages.map((p, i) =>
        p === "…" ? (
          <span key={`gap-${i}`} className="pg-ellipsis" aria-hidden="true">
            …
          </span>
        ) : (
          <button
            key={p}
            className={`pg-btn ${page === p ? "active" : ""}`}
            onClick={() => go(p)}
            aria-label={`Page ${p}`}
            aria-current={page === p ? "page" : undefined}
          >
            {p}
          </button>
        )
      )}

      <button
        className="pg-btn"
        onClick={() => go(page + 1)}
        disabled={page === totalPages}
        aria-label="Next page"
      >
        <i className="ti ti-chevron-right" style={{ fontSize: "12px" }}></i>
      </button>
    </div>
  );

  return (
    <div
      className="pagination"
      style={{ justifyContent: leftSlot ? "space-between" : "flex-end", ...style }}
    >
      {leftSlot && <div className="pg-total">{leftSlot}</div>}
      {nav}
    </div>
  );
}
